import { getCurrentProductItem, getAttributeValue, getAttributeId, isPrimeurCode } from '@/utils/productUtils';

/**
 * classe datalayer service
 */
export default class DatalayerService {
  started = false;
  pending = [];

  constructor() {
    if (typeof window !== 'undefined' && !window['dataLayer']) {
      window['dataLayer'] = [];
    }
  }

  /**
   * function to push data either in the real dataLayer or to a temporary array if storeConf is yet unknown
   * @param {object} object
   * @param {boolean} clear prevent recursive merge of DataLayer's values
   */
  push(object, clear = false) {
    if (clear && object && typeof object === 'object') {
      object._clear = true;
    }

    if (this.started) {
      window['dataLayer'].push(object);
    } else {
      this.pending.push(object);
    }
  }

  /**
   * function to start the dataLayer initialization and push all pending data
   * @param {string} site
   */
  initialize(site) {
    this.started = true;
    this.push({ 'Data Layer Name': 'MillesimaDatalayer' });
    this.push({ site });
    for (const item of this.pending) {
      this.push(item);
    }
    this.pending = [];
  }

  /**
   * function to push event
   * @param {string} key
   * @param {string} value
   */
  pushData(key, value) {
    this.push({ [key]: value });
  }

  /**
   * function to push page name
   * @param {string} event
   * @param {string} pageName
   */

  pushPageName(event, pageName) {
    this.push({ event: event, pageName: pageName });
  }

  /**
   * function to push page name
   * @param {string} event
   */

  pushEvent(event) {
    this.push({ event: event });
  }

  /**
   * function to push custom error CS
   * @param {string} errorKey
   * @param {object} errorMessage
   */
  pushCSCustomError(errorKey, errorMessage) {
    this.push({
      event: 'CS_CUSTOM_ERROR',
      errorKey,
      errorMessage
    });
  }

  /**
   * function to push lastAdded item
   * @param {string} action
   * @param {object} product
   * @param {string} currency
   * @param {number} quantityAdded
   */
  pushLastAdded(action, product, currency, quantityAdded) {
    // TODO: repair "Cannot read property length of undefined" error
    // console.warn('TODO: pushLastAdded must be corrected')

    this.prepareProduct(product, currency);
    this.push({ event: action, lastAdded: { lastAdded: product, quantityAdded: quantityAdded } });
  }

  /**
   * function to push event item
   * @param {array} item
   */
  setQuickViewProduct(item) {
    this.push({ event: 'QuickViewProduct', items: [item] });
  }

  /**
   * function tu push product
   * @param {string} type
   * @param {array} items
   * @param {string} product
   */
  pushProduct(type, items, product) {
    this.push({
      event: type,
      pageName: type,
      items: items,
      productName: product?.productName,
      productPartNumber: product?.partnumber
    });
  }

  /**
   * fuction to push categ
   * @param {string} type
   * @param {string} sortBack
   * @param {string} page
   * @param {object} categ
   * @param {array} items
   */
  pushCateg(type, sortBack, page, categ, items) {
    this.push({
      event: type,
      sortBack: sortBack,
      pageName: type,
      page: page,
      categorieId: categ.groupIdentifier,
      categorie: categ.name,
      items: items,
      breadcrumb: categ.breadcrumbSeo
    });
  }

  /**
   * function to push categ search
   * @param {string} type
   * @param {string} sortBack
   * @param {string} page
   * @param {string} nbTotalRows
   * @param {string} searchTerm
   * @param {string} items
   */
  pushSearch(type, sortBack, page, nbTotalRows, searchTerm, items) {
    this.push({
      event: type,
      sortBack: sortBack,
      pageName: type,
      page: page,
      categorie: type,
      searchResult: {
        nbResult: nbTotalRows,
        searchTerms: searchTerm
      },
      items: items
    });
  }

  /**
   * function to push search term and the categ/product selected
   * @param {string} type
   * @param {string} groupIdentifier
   * @param {string} searchTerm
   * @param {string} url
   */
  pushSearchSuggest(type, groupIdentifier, searchTerm, url) {
    this.push({ event: 'searchSuggest', searchSuggest: { type, groupIdentifier, searchTerm, url } });
  }

  /**
   * function to push promotion click
   * @param {string} id
   * @param {string} name
   * @param {string} creative
   * @param {string} position
   */
  pushPromotionClick(id, name, creative, position) {
    this.push({
      event: 'promotionClick',
      ecommerce: {
        promoClick: {
          promotions: [{
            id: id, // Nom de la promo , content name -DP
            name: name, // le alt de la balise image
            creative: creative, // DescenteProduit ou ContentCarousel(slide)
            position: position // le 1 ou le 2 a passer en param
          }]
        }
      }
    });
  }

  /**
   * function to push productcard
   * @param {string} brand
   * @param {string} categBread
   * @param {string} partnumber
   * @param {string} name
   * @param {string} price
   * @param {string} variant
   */
  pushProductCard(brand, categBread, partnumber, name, price, variant) {
    this.push({
      event: 'productClick',
      ecommerce: {
        click: {
          products: [{
            brand, // Nom de la promo , content name -DP
            category: categBread, // le alt de la balise image
            id: partnumber, // DescenteProduit ou ContentCarousel(slide)
            name, // le 1 ou le 2 a passer en param
            position: '',
            price,
            variant,
          }]
        }
      }
    });
  }

  /**
  * function to push menu click
  * @param {string} chapter1
  * @param {string} chapter2
  * @param {string} chapter3
  */
  pushMenuClick(chapter1, chapter2, chapter3) {
    this.push({
      event: 'menuClick',
      ecommerce: {
        menuClick: {
          menuChapters: [{
            chapter1: chapter1,
            chapter2: chapter2,
            chapter3: chapter3
          }]
        }
      }
    });
  }

  /**
  * function to push carousel click
  * @param {object} product
  */
  pushProductCarouselClick(product) {
    this.push({
      event: 'productCarouselClick',
      ecommerce: {
        productCarouselClick: {
          products: [{
            id: product._id,
            name: product.productName,
            url: product.seoKeyword
          }]
        }
      }
    });
  }

  prepareProduct(products, currency) {
    const productDataLayer = [];
    for (let i = 0; i < products.length; i++) {
      const packaging = products[i].product.item.attributes ? products[i].product.item.attributes.packaging.value : '';
      const nbunit = products[i].product.item.attributes ? products[i].product.item.attributes.nbunit.value : '';
      const format = products[i].product.item.attributes ? products[i].product.item.attributes.format.value : '';
      const country = products[i].product.attributes.country ? products[i].product.attributes.country.value : '';
      const region = products[i].product.attributes.country ? products[i].product.attributes.region.value : '';
      const appellation = products[i].product.attributes.country ? products[i].product.attributes.appellation.value : '';
      const producteur = products[i].product.attributes.producteur ? products[i].product.attributes.producteur.value : '';

      const items = [{
        brand: producteur,
        category: country + '/' + region + '/' + appellation,
        contentGroup2: country,
        contentGroup3: region,
        contentGroup4: appellation,
        currency: currency,
        dimension2: '0.0',
        id: products[i].product.item.partnumber,
        name: products[i].product.productName,
        quantity: products[i].quantity,
        price: products[i].product.item.offerPrice,
        variant: packaging + ' ' + nbunit + ' ' + format,
      }];

      productDataLayer.push(items);
    }
    return productDataLayer;
  }

  pushUser(
    user, loggedUser, actAsCustomer, { hashMD5, hashSha256 }: {hashMD5: string, hashSha256: { email: string, pickId: string }}
  ) {
    this.push({
      event: 'UpdateUser',
      user: user ? {
        email: user.email,
        user_email_sha256: hashSha256.email,
        user_email_md5: hashMD5,
        firstname: user.firstName,
        lastname: user.lastName,
        telephone: user.phoneNumber,
        userId: user._id,
        guest: false,
        level: user.level,
        isAdmin: loggedUser?.isAdmin,
        isActAs: actAsCustomer ? true : false,
        pickId: user.pickId,
        pickId_sha256: hashSha256.pickId,
        client: user.lastOrderDate ? true : false
      } : {
        email: '',
        user_email_sha256: '',
        user_email_md5: '',
        firstname: '',
        lastname: '',
        telephone: '',
        userId: '-1002',
        guest: true,
        level: '',
        isAdmin: '',
        isActAs: '',
        pickId: '',
        client: false
      }
    });
  }

  pushCart(cart, currency, isNewCart = false) {
    const cartItems = this.prepareItems(cart.products, currency);
    this.push({
      event: isNewCart ? 'CreateCart' : 'UpdateCart',
      cart: {
        cartItems,
        currency,
        orderId: cart.id,
        totalTTC: cart.total
      },
      _clear: true // Prevent deep-merging of previous DataLayer values
    });
  }

  pushOrder(cart, currency) {
    const codesPromo = [];
    const cartItems = this.prepareItems(cart.products, currency) || [];
    const totalProductPriceTTC = cart.totalProductHT + cart.totalTVA;
    const shippingMode = cart.shippingMode ? cart.shippingMode.title : '';
    const paiementType = cart.paiementMode ? cart.paiementMode.pickId : '';
    if (cart?.discountCode?.name) {
      codesPromo.push(cart.discountCode.name);
    }
    this.push({
      order: {
        cartItems,
        codesPromo,
        currency,
        offer: cart.totalDiscountOrder,
        orderId: cart.id,
        shippingHT: '',
        shippingMode,
        shippingTTC: cart.shippingPrice,
        paiementType,
        totalHT: cart.totalProductHT,
        totalProductPriceHT: cart.totalProductHT,
        totalProductPriceTTC,
        totalTTC: cart.total
      },
      _clear: true // Prevent deep-merging of previous DataLayer values
    });
  }

  pushOrderAndPageName(cart, currency, pageName, user) {
    const totalProductPriceTTC = cart.totalProductHT + cart.totalTVA;
    const shippingMode = cart.shippingMode ? cart.shippingMode.title : '';
    const billingMode = cart.paiementMode ? cart.paiementMode.name : '';
    const paiementType = cart.paiementMode ? cart.paiementMode.pickId : '';
    const codesPromo = cart.discountCode ? [cart.discountCode] : [];
    const cartItems = this.prepareItems(cart.products, currency);

    this.push({
      pageName,
      event: pageName,
      user: {
        email: user.email,
        firstname: user.firstName,
        lastname: user.lastName,
        telephone: user.phoneNumber,
        userId: user._id,
        guest: false
      },
      order: {
        cartItems,
        codesPromo,
        currency,
        offer: cart.totalDiscountOrder,
        orderId: cart.id,
        isFirstOrder: cart.isFirstOrder,
        shippingHT: '',
        shippingMode,
        shippingTTC: cart.shippingPrice,
        billingMode,
        paiementType,
        totalHT: cart.totalProductHT,
        totalProductPriceHT: cart.totalProductHT,
        totalProductPriceTTC,
        totalTTC: cart.total
      },
      billing: {
        phone_number: cart.billing.phoneNumber,
        email: user.email,
        address: {
          first_name: cart.billing.firstName,
          last_name: cart.billing.lastName,
          street: cart.billing.street,
          city: cart.billing.city,
          postal_code: cart.billing.postalCode,
          country: cart.billing.country,
          region: ''
        }
      },
    }, true);
  }

  prepareItems(products, currency) {
    return products.map(({ product, price, quantity, discountPrice }) => {
      const packaging = getAttributeValue(product.item, 'packaging', '');
      const nbunit = getAttributeValue(product.item, 'nbunit', '');
      const format = getAttributeValue(product.item, 'format', '');
      const country = getAttributeValue(product, 'pays', '');
      const region = getAttributeValue(product, 'region', '');
      const appellation = getAttributeValue(product, 'appellation', '');
      const brand = getAttributeValue(product, 'producteur', '');
      const inStock = getAttributeValue(product, 'in_stock', '');

      return {
        quantity,
        item: {
          SKU: product.item.partnumber,
          appellation,
          brand,
          country,
          currency,
          imageUrl: product.fullimage,
          isPrimeur: inStock,
          parentUniqueID: product._id,
          price: price / quantity,
          productName: product.productName,
          region,
          strikedPrice: discountPrice < price ? discountPrice / quantity : '',
          uniqueID: product.item._id,
          variant: packaging + ' ' + nbunit + ' ' + format,
        }
      };
    });
  }

  /**
   * function to push product
   * @param {product} product
   * @param {item} currentItem
   */
  refreshProductDataLayer(product, currentItem) {
    const itemsDatalayer = [];
    const items = (product.itemsCP || []).concat(product.items || []); // TODO: delete ItemsCP and add dataLayer event for CP ?
    if (items.length > 0) {
      itemsDatalayer.push(this.getItems(currentItem || items[0], product));
    }
    this.pushProduct('ProductPage', itemsDatalayer, product);
  }

  pushViewedProducts(products, filterQuery, sort, categ, searchMode, storeCode) {
    const viewedItems = [];
    if (products) {
      products.forEach((product) => {
        const currentItem = getCurrentProductItem(product, filterQuery, sort, categ, searchMode, storeCode, false);
        viewedItems.push(this.getItems(currentItem, product));
      });
      this.push({ event: 'viewedItems', viewedItems: viewedItems });
    }
  }
  /**
   * update categ datalayer
   * @param {array} products
   * @param {string} filterQuery
   * @param {string} sort
   * @param {string} sortBack
   * @param {string} categ
   * @param {string} searchMode
   * @param {string} storeCode
   * @param {string} nbTotalRows
   * @param {string} searchTerm
   * @param {boolean} isWinebox
   */
  updateCategDatalayer(
    products,
    filterQuery,
    sort,
    sortBack,
    categ,
    searchMode,
    storeCode,
    nbTotalRows,
    searchTerm,
    isWinebox = false
  ) {
    const items = [];

    if (products) {
      products.forEach((product) => {
        const currentItem = getCurrentProductItem(product, filterQuery, sort, categ, searchMode, storeCode, isWinebox);
        items.push(this.getItems(currentItem, product));
      });
      this.pushData('items', items);
    }

    if (!searchMode) {
      this.pushCateg('Listing', sortBack, 'Listing', categ, items);
    } else {
      this.pushSearch('Search', sortBack, 'Search', nbTotalRows, searchTerm, items);
    }
  }

  /**
   * function to return item info;
   * @param {array} currentItem
   * @param {array} product
   * @return {array }items
   */
  getItems(currentItem, product) {
    let cond;
    let nbunit = 1;
    if (currentItem && currentItem.attributes) {
      nbunit = getAttributeValue(currentItem, 'nbunit', 1);
      cond = getAttributeValue(currentItem, 'nbunit', '');
    }

    let isPrimeur;
    const primeurCode = getAttributeId(product, 'in_stock', undefined);
    if (isPrimeurCode(primeurCode)) {
      isPrimeur = true;
    } else {
      isPrimeur = false;
    }

    return {
      SKU: currentItem?.partnumber,
      appellation: getAttributeValue(product, 'appellation', ''),
      brand: getAttributeValue(product, 'producteur', ''),
      country: getAttributeValue(product, 'pays', ''),
      curency: product.currency,
      imageUrl: product.fullimage,
      isPrimeur: isPrimeur,
      price: currentItem?.offerPrice,
      productName: product.productName,
      region: getAttributeValue(product, 'region', ''),
      strikedPrice: '',
      variant: nbunit + ' * ' + cond,
      breadcrumb: product.breadcrumbSeo
    };
  }

  pushChatbotEvent(value) {
    this.push({ event: 'Chatbot', chatbot: value });
  }
}
